import { useMemo } from 'react';
import type { QueryClient } from '@zg-rentals/react-query5';
import {
  useCappsApplicationsWebApiHandler_getRentalApplication,
  useCappsRenterUserStatusesWebApiHandler_getUserStatuses,
} from '@zg-rentals/ts-capps-client-react-query';
import { toRentalApplication } from '../utils/capps-mappers';

export const RENTAL_APPLICATION_TAG = 'rentalApplication';
export const USER_STATUSES_TAG = 'userStatuses';

/**
 * Refetches the application and the user statuses, matching what the old
 * GetRentalApplication graphql query used to return in a single response
 */
export function invalidateRentalApplication(queryClient: QueryClient) {
  return queryClient.invalidateQueries({
    predicate: ({ meta }) =>
      (meta?.tags?.includes(RENTAL_APPLICATION_TAG) || meta?.tags?.includes(USER_STATUSES_TAG)) ?? false,
  });
}

export function useRentalApplication(applicationId: string | undefined) {
  const { data, isPending, isError } = useCappsApplicationsWebApiHandler_getRentalApplication(
    { id: applicationId ?? '' },
    {
      enabled: !!applicationId,
      meta: {
        tags: [RENTAL_APPLICATION_TAG],
        context: { api: 'cappsApplications', details: 'getRentalApplication' },
      },
    },
  );

  const application = useMemo(() => (data ? toRentalApplication(data) : undefined), [data]);

  return { application, isPending, isError };
}

export function useUserStatuses({ enabled = true }: { enabled?: boolean } = {}) {
  const { data, isPending, isError } = useCappsRenterUserStatusesWebApiHandler_getUserStatuses({
    enabled,
    meta: {
      tags: [USER_STATUSES_TAG],
      context: { api: 'cappsRenterUserStatuses', details: 'getUserStatuses' },
    },
  });

  return { userStatuses: data?.userStatuses, isPending, isError };
}
